import * as React from 'react';
import { Link, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    TabContent,
    TabPane
} from 'reactstrap';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRightArrowLeft,
    faCircleInfo,
    faEllipsisVertical,
    faEyeSlash,
    faShieldHalved,
    faTableList,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import {
    GetFinancialStatementsEnabled,
    GetRiskCalcEnabled,
    GetDisableAdditionalDocUpload,
    GetDisableProvideuserCopy,
    GetDisableBasisOfAccounting,
    GetDisableAnonymizeResults,
} from '../../Store/AppSettings';
import {
    actionCreators as SubmissionHistoryActions,
    ActiveSubmissionDetails,
    GetActiveSubmissions,
    
} from '../../Store/SubmissionHistory';
import {
    actionCreators as FinancialStatementsActions,
    FinancialStatementWorkbookRegenerationTracking,
    GetWorkbookRegenerationTracking,
} from '../../Store/FinancialStatements'
import { GetMinMappingDate } from '../../Store/GlobalSettings';

import { ApplicationState } from '../../Store';
import { GetPortalBusyState, actionCreators as UIStateActions } from '../../Store/UIState';
import { GetPortalText, RetrieveText } from '../../Store/PortalAssets';

import {
    SubmissionDetail,
    PortalTextCommon,
    PortalTextSubmissionDetails,
    PortalTextSubmissionRequestSummary,
} from '../../Models/Api/strongbox.financialportal';

import { pathConstants, routeConstants, SubmissionDetailsTabNames } from '../../Utils/Constants';

import './Overview/SubmissionOverview.scss';

import { SubmissionOverview } from './Overview/SubmissionOverview';
import { RiskAnalysis } from './RiskAnalysis/RiskAnalysis';

import { 
    DeleteSubmission,
    GetSubmissionDetail, 
    SearchSubmissions 
} from '../../Services/SubmissionService';

import { ErrorState, ErrorBanner } from '../ErrorBanner/ErrorBanner';
import { ConfirmModal, ConfirmModalType } from '../ConfirmModal/ConfirmModal';

import { BuildQueryString } from '../../Utils/WorkspaceRouteInformation';

import { LogMessage, SeverityLevel } from '../../Utils/Logging';
import { FormatDate, formatStringMDYFullMonth } from '../../Utils/DateUtils';

import { BreadCrumb, BreadCrumbs } from '../BreadCrumbs/BreadCrumbs';
import { FinancialStatementEditor } from './FinancialStatements/FinancialStatementEditor';

// 2 seconds
const updateRegenerationStatusTimeout = 2000;

type InjectedReduxState = {
    activeSubmissionDetails: ActiveSubmissionDetails[];
    financialStatementsEnabled: boolean;
    riskCalcEnabled: boolean;
    portalBusy: boolean;
    fileUploadDisabled: boolean;
    userCopyDisabled: boolean;
    anonymizeDisabled: boolean;
    basisOfAccountingDisabled: boolean;
    minValidDateForMapping?: Date;
    revisionTracking: FinancialStatementWorkbookRegenerationTracking[];
    commonText?: PortalTextCommon;
    submissionDetailsText?: PortalTextSubmissionDetails;
    requestSummaryText?: PortalTextSubmissionRequestSummary;
};

type InjectedActionCreators = typeof SubmissionHistoryActions & typeof FinancialStatementsActions & typeof UIStateActions;

type SubmissionDetailsProps = {
}

type Props = SubmissionDetailsProps & InjectedActionCreators & InjectedReduxState;

export const idSubmissionDetailsContainerWindow = 'submission-details-container';

const SubmissionDetailsComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        financialStatementsEnabled,
        activeSubmissionDetails,
        SetActiveSubmissionDetails,
        riskCalcEnabled,
        portalBusy,
        ClearCompletedRevisionTracking,
        UpdateWorkbookRegenerationStatuses,
        revisionTracking,
        minValidDateForMapping,
        SubmissionDeleted,
        commonText,
        SetPortalIdle,
        SetPortalWorking,
        submissionDetailsText,
        requestSummaryText,
    } = props;

    const location = useLocation();
    const navigate = useNavigate();

    // parameter name, i.e. 'submissionId' should match the value in routeConstants.submissionIdParam
    // workspaceId should match value in routeConstants.workspaceIdParam
    const {
        submissionId,
        workspaceId,
    } = useParams();

    const [activeSubmission, setActiveSubmission] = React.useState<ActiveSubmissionDetails | undefined>(undefined);
    const [matchedPath, setMatchedPath] = React.useState<string>('');
    const [rootPath, setRootPath] = React.useState<string>('/');

    const [returnCrumbs, setReturnCrumbs] = React.useState<BreadCrumb[]>([]);

    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);

    const [pageMenuOpen, setPageMenuOpen] = React.useState<boolean>(false);
    const pageMenuRef = React.useRef<HTMLDivElement>(null);
    const [askDelete, setAskDelete] = React.useState<string | undefined>(undefined);
    const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState<boolean>(false);

    const [revisionTrackingDismissed, setRevisionTrackingDismissed] = React.useState<boolean>(false);

    const setAndClearRevisionTrackingDismissed = (dismissed: boolean): void => {
        if (dismissed) {
            ClearCompletedRevisionTracking();
        }
        setRevisionTrackingDismissed(dismissed);
    }

    const pathParameters = React.useMemo(() => {
        return `${!!workspaceId ? `/${escape(workspaceId)}` : ''}/${escape(submissionId || '')}`;
    }, [submissionId, workspaceId]);

    const loadActiveSubmission = async (): Promise<void> => {
        const loadSubmissionWorking = 'submissionDetails:loadingActiveSubmission';

        // Sanity check, shouldn't be possible
        if (!(workspaceId && submissionId)) {
            return;
        }

        try {
            SetPortalWorking(loadSubmissionWorking);

            let submission: SubmissionDetail;

            try {
                submission = await GetSubmissionDetail(workspaceId, submissionId);
            } catch (exception) {
                setErrorState({
                    severity: 'Error',
                    summaryMessage: submissionDetailsText?.errorMsgCantResolveDC || '',
                    extraInformation: submissionDetailsText?.errorMsgEnsureDCPermissions || '',
                });
                return;
            }

            const startDate = new Date(submission!.creationTime);
            const endDate = new Date(submission!.creationTime);

            startDate.setDate(startDate.getDate() - 1);
            endDate.setDate(endDate.getDate() + 1);

            const submissionList = await SearchSubmissions(startDate, endDate, workspaceId);
            const ssr = submissionList.find(sub => sub.submission.id === submissionId);

            if (!ssr) {
                // shouldn't be possible
                LogMessage(
                    `Unexpected state in SubmissionDetails: Unable to retrieve submission search request from list of active requests, submissionId: ${submissionId}`,
                    SeverityLevel.Warning,
                    {
                        submissionId,
                        searchStart: startDate.toString(),
                        searchEnd: endDate.toString()
                    }
                );
                console.error('Unexpected state: SubmissionDetails unable to find submission search request for this page');
                navigate(pathConstants.home);
                return;
            }

            const activeSub: ActiveSubmissionDetails = {
                submissionSearchResult: ssr,
                submissionDetails: submission,
            }

            props.SetActiveSubmissionDetails(activeSub);

            setActiveSubmission(activeSub);
        } finally {
            SetPortalIdle(loadSubmissionWorking);
        }
    }

    const retrieveSubmissionDetailsText = async (): Promise<void> => {
        const workingMsg = 'submissionDetails:RetrieveText';

        try {
            SetPortalWorking(workingMsg);
            
            await RetrieveText(['submissionDetails']);
        } finally {
            SetPortalIdle(workingMsg);
        }
    }

    React.useEffect(() => {
        retrieveSubmissionDetailsText();

        if (!submissionId) {
            LogMessage(
                `Unexpected state in SubmissionDetails: submissionId is undefined, navigating to ${pathConstants.home}`,
                SeverityLevel.Warning,
            );
            console.error('Unexpected state: SubmissionDetails has no submission id in url, redirecting to submissions list');
            navigate(pathConstants.home);
        } else {
            const submission = activeSubmissionDetails.find(ad => ad.submissionSearchResult.submission.id === submissionId);
            if (!submission) {
                // Gotta have the workspaceId for this to work.
                if (!workspaceId) {
                    navigate(pathConstants.home);
                } else {
                    loadActiveSubmission();
                }
            } else {
                setActiveSubmission(submission);
            }
        }
        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Switching language clears every loaded text section; fetch this page's section again.
    const submissionDetailsTextLoaded = !!submissionDetailsText;
    const textMounted = React.useRef(false);
    React.useEffect(() => {
        if (textMounted.current && !submissionDetailsTextLoaded) {
            retrieveSubmissionDetailsText();
        }
        textMounted.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionDetailsTextLoaded]);

    const updateRevisionTracking = (): void => {
        UpdateWorkbookRegenerationStatuses(loadActiveSubmission)
    }

    React.useEffect(() => {
        if ((revisionTracking.length > 0) && (!revisionTrackingDismissed)) {
            const revisionStatusTimerId = setInterval(updateRevisionTracking, updateRegenerationStatusTimeout);

            return () => { clearInterval(revisionStatusTimerId) }
        }

        // This is in fact the dependency list I want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionTracking, revisionTrackingDismissed])

    const submissionSupportsFinancialStatements = React.useMemo(() => {
        if (!(activeSubmission && activeSubmission.submissionDetails)) {
            return false;
        }
        if (!minValidDateForMapping) {
            return true;  // might as well go for it.  There are enough safeguards before this that we should have 
                          // confidence the setting is valid in the back end configuration
        }
        const submissionDate = new Date(activeSubmission.submissionDetails.creationTime);
        return submissionDate > minValidDateForMapping;

        // This is in fact the dependency list I want.  minValidDateForMapping won't actually change during a run
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubmission]);

    const setActiveSubmissionDetails = (details: ActiveSubmissionDetails): void => {
        SetActiveSubmissionDetails(details);
        setActiveSubmission(details);
    }

    const isCurrentTab = (paths: string[]): boolean => paths.indexOf(matchedPath) !== -1;

    // Dismiss the page actions menu on an outside click or Escape.
    React.useEffect(() => {
        if (!pageMenuOpen) {
            return;
        }
        const onMouseDown = (e: MouseEvent): void => {
            if (pageMenuRef.current && !pageMenuRef.current.contains(e.target as Node)) {
                setPageMenuOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setPageMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [pageMenuOpen]);

    // The details drawer only exists below the sidebar breakpoint (kept in sync with the
    // max-width: 1099px media query in SubmissionOverview.scss): close it when the viewport widens.
    React.useEffect(() => {
        if (!detailsDrawerOpen) {
            return;
        }
        const mediaQuery = window.matchMedia('(min-width: 1100px)');
        const onChange = (e: MediaQueryListEvent): void => {
            if (e.matches) {
                setDetailsDrawerOpen(false);
            }
        };
        mediaQuery.addEventListener('change', onChange);
        return () => mediaQuery.removeEventListener('change', onChange);
    }, [detailsDrawerOpen]);

    const navToLatestRevision = (): void => {
        navigate(`${routeConstants.workspaceSubmissionDetails}/overview/${workspaceId}/${submissionId}`);
    }

    const getTabContent = (): React.ReactElement => {
        switch (matchedPath) {
            case pathConstants.workspaceSubmissionDetailsOverview:
            case pathConstants.allSubmissionDetailsOverview:
                return (
                    <TabPane key={'submissiondetailsoverviewtab'} tabId={routeConstants.submissionDetailsOverview}>
                        {activeSubmission && (
                            <SubmissionOverview
                                financialStatementsEnabled={financialStatementsEnabled}
                                activeDetails={activeSubmission}
                                setActiveSubmissionDetails={setActiveSubmissionDetails}
                                detailsDrawerOpen={detailsDrawerOpen}
                                onCloseDetailsDrawer={() => setDetailsDrawerOpen(false)}
                                commonText={commonText}
                                submissionDetailsText={submissionDetailsText}
                            />
                        )}
                    </TabPane>
                );
            case pathConstants.workspaceSubmissionDetailsFinancialStatements:
            case pathConstants.allSubmissionDetailsFinancialStatements:
                return (
                    <TabPane key={'submissiondetailsbalancesheettab'} tabId={routeConstants.submissionDetailsFinancialStatements}>
                        {activeSubmission && (
                            <FinancialStatementEditor
                                financialRecordId={activeSubmission.submissionDetails.financialRecordId || ''}
                                orgId={activeSubmission.submissionDetails.entityId}
                                revisionTrackingDismissed={revisionTrackingDismissed}
                                setRevisionTrackingDismissed={setAndClearRevisionTrackingDismissed}
                                revisionTracking={revisionTracking}
                                submissionSupportsFinancialStatements={submissionSupportsFinancialStatements}
                                onNavToLatestRevision={navToLatestRevision}
                            />
                        )}
                    </TabPane>
                );
            case pathConstants.workspaceSubmissionDetailsRiskAnalysis:
            case pathConstants.allSubmissionDetailsRiskAnalysis:
                // Shouldn't be possible to get here if there's no financial record ID, i.e. we shouldn't be showing the tab
                // if it isn't part of the activeSubmission.
                return (
                    <TabPane key={'businesssubmissiondetailsriskanalysistab'} tabId={routeConstants.submissionDetailsRiskAnalysis}>
                        {activeSubmission && !!activeSubmission.submissionSearchResult.submission.financialRecordId && !portalBusy && (
                            <RiskAnalysis
                                businessName={activeSubmission?.submissionSearchResult.entity.displayName || ''}
                                entityId={workspaceId || ''}
                                financialRecordId={activeSubmission!.submissionSearchResult.submission.financialRecordId}
                            />
                        )}
                    </TabPane>
                )
        }
        return (<></>);
    }

    React.useEffect(() => {
        let workspaceReturnLink = '';
        let allReturnLink = pathConstants.dataCollectionReport;
        let workspaceDisplayName = '';

        if (!!activeSubmission) {
            const workspaceId = activeSubmission.submissionSearchResult.entity.id;
            const workspaceName = activeSubmission.submissionSearchResult.entity.displayName;

            if (!!workspaceName) {
                workspaceDisplayName = workspaceName;
            }

            workspaceReturnLink = `${pathConstants.workspaceDetailsSubmissionList}/${BuildQueryString({ workspaceId: workspaceId, workspaceName: workspaceName || '' })}`;
        }

        switch (matchedPath) {
            case pathConstants.workspaceSubmissionDetailsOverview:
            case pathConstants.workspaceSubmissionDetailsFinancialStatements:
            case pathConstants.workspaceSubmissionDetailsRiskAnalysis:
                setReturnCrumbs([
                    {
                        content: commonText?.breadcrumbWorkspaceList || '',
                        link: pathConstants.home
                    },
                    {
                        content: workspaceDisplayName,
                        link: workspaceReturnLink
                    },
                    {
                        content: submissionCollectedOnDate
                    }
                ]);
                break;
            default:
                setReturnCrumbs([
                    {
                        content: commonText?.breadcrumbWorkspaceList || '',
                        link: pathConstants.home
                    },
                    {
                        content: submissionDetailsText?.dataCollectionReport || '',
                        link: allReturnLink
                    },
                    {
                        content: submissionCollectedOnDate
                    }
                ]);
                break;
        }

        // These are the only depedndencies I want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubmission, matchedPath])

    const submissionCollectedOnDate = React.useMemo(() => {
        if (!activeSubmission) {
            return '';
        }

        // These are the only depedndencies I want
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return FormatDate(new Date(activeSubmission.submissionDetails.creationTime), formatStringMDYFullMonth);
    }, [activeSubmission])

    const activeTab: SubmissionDetailsTabNames | undefined = React.useMemo(() => {
        // submissionId will not be undefined here.
        const pathToMatch = location.pathname.replace(`${!!workspaceId ? `/${escape(workspaceId)}` : ''}/${escape(submissionId || '')}`, '');
        setMatchedPath(pathToMatch);

        let result: SubmissionDetailsTabNames;

        switch (pathToMatch) {
            case pathConstants.workspaceSubmissionDetailsOverview:
            case pathConstants.allSubmissionDetailsOverview:
                result = 'overview';
                break;
            case pathConstants.workspaceSubmissionDetailsFinancialStatements:
            case pathConstants.allSubmissionDetailsFinancialStatements:
                result = 'financialstatements';
                break;
            case pathConstants.workspaceSubmissionDetailsRiskAnalysis:
            case pathConstants.allSubmissionDetailsRiskAnalysis:
                result = 'riskanalysis';
                break;
            default:
                setRootPath('/');
                return undefined;
        }

        setRootPath(pathToMatch.replace(`/${result}`, ''));

        return result;
    }, [location.pathname, workspaceId, submissionId])

    if (activeTab === undefined) {
        return (<Navigate to={pathConstants.notFound} />)
    }

    const deleteSubmission = (submissionId: string): void => {
        DeleteSubmission(submissionId)
            .then(() => {
                if (!!workspaceId) {
                    SubmissionDeleted(workspaceId, submissionId);
                    navigate(`${pathConstants.workspaceDetailsSubmissionList}/${workspaceId}`);
                }
            })
            .catch(() => {
                setErrorState({
                    severity: 'Error',
                    summaryMessage: submissionDetailsText?.errorMsgCantDeleteSubmission || '',
                });
            })
    }

    const overviewPaths = [pathConstants.workspaceSubmissionDetailsOverview, pathConstants.allSubmissionDetailsOverview];
    const isOverview = overviewPaths.indexOf(matchedPath) !== -1;

    // The collection's views are reached from the page actions menu, not from a row of tab buttons, so the
    // title bar is the same at every width. Each view is offered under the same conditions the tabs used.
    const pageNavItems: { key: string; label: string; icon: IconDefinition; to: string; paths: string[] }[] = [
        {
            key: 'overview',
            label: submissionDetailsText?.tabTitleOverview || '',
            icon: faTableList,
            to: `${rootPath}/${routeConstants.submissionDetailsOverview}${pathParameters}`,
            paths: overviewPaths,
        },
        ...(props.financialStatementsEnabled ? [{
            key: 'financial-statements',
            label: submissionDetailsText?.mapFinancialStatements || 'Map financial statements',
            icon: faArrowRightArrowLeft,
            to: `${rootPath}/${routeConstants.submissionDetailsFinancialStatements}${pathParameters}`,
            paths: [pathConstants.workspaceSubmissionDetailsFinancialStatements, pathConstants.allSubmissionDetailsFinancialStatements],
        }] : []),
        ...((riskCalcEnabled && !!activeSubmission && !!activeSubmission.submissionSearchResult.submission.financialRecordId) ? [{
            key: 'risk-analysis',
            label: submissionDetailsText?.tabTitleRiskAnalysis || '',
            icon: faShieldHalved,
            to: `${rootPath}/${routeConstants.submissionDetailsRiskAnalysis}${pathParameters}`,
            paths: [pathConstants.workspaceSubmissionDetailsRiskAnalysis, pathConstants.allSubmissionDetailsRiskAnalysis],
        }] : []),
    ];

    const pageMenu = (!!activeSubmission && !!submissionDetailsText) ? (
        <div className={'page-alt-page-menu'} ref={pageMenuRef}>
            <button
                type={'button'}
                className={'cr-btn-icon cr-btn-icon--floating'}
                aria-label={submissionDetailsText?.collectionActions || 'Collection actions'}
                aria-haspopup={'menu'}
                aria-expanded={pageMenuOpen}
                onClick={() => setPageMenuOpen(!pageMenuOpen)}
            >
                <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
            {pageMenuOpen && (
                <div className={'cr-menu page-alt-page-menu__menu'} role={'menu'}>
                    {pageNavItems.map(item => {
                        const current = isCurrentTab(item.paths);
                        return (
                            <Link
                                key={item.key}
                                to={item.to}
                                role={'menuitem'}
                                className={`cr-menu-item${current ? ' cr-menu-item--active' : ''}`}
                                aria-current={current ? 'page' : undefined}
                                onClick={() => setPageMenuOpen(false)}
                            >
                                <span className={'cr-menu-item__leading'}><FontAwesomeIcon icon={item.icon} /></span>
                                <span className={'cr-menu-item__label'}>{item.label}</span>
                            </Link>
                        );
                    })}
                    {isOverview && (
                        <>
                            <div className={'cr-menu-separator'} role={'separator'} />
                            <button
                                type={'button'}
                                role={'menuitem'}
                                className={'cr-menu-item'}
                                onClick={() => {
                                    setPageMenuOpen(false);
                                    setAskDelete(activeSubmission.submissionSearchResult.submission.id);
                                }}
                            >
                                <span className={'cr-menu-item__leading'}><FontAwesomeIcon icon={faEyeSlash} /></span>
                                <span className={'cr-menu-item__label'}>{submissionDetailsText?.hideSubmission || ''}</span>
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    ) : undefined;

    return (
        <div className={'page-overview'} id={idSubmissionDetailsContainerWindow}>
            <div className={'page-workspace-content'}>
                {
                    errorState && (
                        <div className={'control-region control-region-lender'}>
                            <ErrorBanner
                                errorState={{...errorState, actions:[
                                    {
                                        text: commonText?.dismiss || '',
                                        id: 'dismissAction',
                                        onAction: (actionId: string) => setErrorState(undefined)
                                    }
                                ]}}
                                onDefaultActionButton={() => setErrorState(undefined)}
                                commonText={commonText}
                            />
                        </div>
                    )
                }
                <div className={`page-workspace-topbar page-alt-topbar${pageMenuOpen ? ' page-alt-topbar--menu-open' : ''}`}>
                    <BreadCrumbs
                        variant={'title'}
                        navLabel={commonText?.breadcrumbNavLabel}
                        crumbs={returnCrumbs}
                        trailing={pageMenu}
                    />
                    {isOverview && (
                        <button
                            type={'button'}
                            className={'cr-btn cr-btn--secondary page-alt-details-btn'}
                            onClick={() => setDetailsDrawerOpen(true)}
                        >
                            <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faCircleInfo} /></span>
                            <span className={'cr-btn__label'}>{submissionDetailsText?.showDetails || 'Show details'}</span>
                        </button>
                    )}
                </div>
                {/* The overview is design-system only; the other tabs still use the legacy control-region styling. */}
                <div className={isOverview ? '' : 'control-region control-region-lender'}>
                    <TabContent
                        activeTab={activeTab}
                        className={isOverview ? 'portal-tab-content' : 'portal-tab-content portal-tab-content-normal'}
                    >
                        {getTabContent()}
                    </TabContent>
                </div>
                <div className={'page-alt-footer'}>
                    <span>{commonText?.portalCopyright || ''}</span>
                </div>
            </div>
            {
                !!askDelete && (
                    <ConfirmModal
                        msg={(
                            <div>
                                <p>{submissionDetailsText?.deleteSubmissionQuestion1 || ''}</p>
                                <p>{submissionDetailsText?.deleteSubmissionQuestion2 || ''}</p>
                            </div>
                        )}
                        title={commonText?.warning || 'Warning'}
                        modalType={ConfirmModalType.yesno}
                        onTerminalButton={(yes?: boolean) => {
                            if (!!yes) {
                                deleteSubmission(askDelete);
                            }
                            setAskDelete(undefined);
                        }}
                        commonText={commonText}
                    />
                )
            }
        </div>
    );
}

export const SubmissionDetails = connect<InjectedReduxState, InjectedActionCreators, SubmissionDetailsProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            activeSubmissionDetails: GetActiveSubmissions(appState),
            financialStatementsEnabled: GetFinancialStatementsEnabled(appState),
            riskCalcEnabled: GetRiskCalcEnabled(appState),
            portalBusy: GetPortalBusyState(appState),
            fileUploadDisabled: GetDisableAdditionalDocUpload(appState),
            userCopyDisabled: GetDisableProvideuserCopy(appState),
            revisionTracking: GetWorkbookRegenerationTracking(appState),
            anonymizeDisabled: GetDisableAnonymizeResults(appState),
            basisOfAccountingDisabled: GetDisableBasisOfAccounting(appState),
            minValidDateForMapping: GetMinMappingDate(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            submissionDetailsText: GetPortalText<PortalTextSubmissionDetails>(appState, 'submissionDetails'),
            requestSummaryText: GetPortalText<PortalTextSubmissionRequestSummary>(appState, 'submissionRequestSummary'),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...SubmissionHistoryActions,
            ...FinancialStatementsActions,
            ...UIStateActions,
        },
        dispatch
    )
)(SubmissionDetailsComponent);
